import { Paginated } from 'types/api';

import { instance } from 'utils/api';

// Wire: camelCase AppointmentFile rows.
export type AppointmentFileRow = {
  id: number;
  appointmentId: number;
  file: string;
  sender?: string;
  createdAt?: string;
};

// GET /appointments/previous-files/:patientId — files attached to APPROVED
// appointments with this patient (last 90 days); bare array.
export const fetchPreviousFilesByPatient = async (
  patientId: number | string
): Promise<AppointmentFileRow[]> => {
  const res = await instance.get<AppointmentFileRow[]>(
    `appointments/previous-files/${patientId}`
  );

  return res.data || [];
};

// ---------------------------------------------------------------------------
// v2 → legacy translator
// ---------------------------------------------------------------------------
// The whole appointments UI was written against the GQL shape (string ids,
// numeric status 1-7, `dates`/`notes`/`files` relations, numeric plan.type).
// v2 emits int ids, text-enum status, appointmentDates/appointmentNotes/
// appointmentFiles, text plan.type. Rather than rewrite every component we
// translate each row back to the legacy shape once, here.

// text status → legacy numeric (constants/appointments)
const STATUS_TEXT_TO_NUM: Record<string, number> = {
  AWAITING_CONFIRMATION: 1,
  APPROVED: 2,
  STARTED: 3,
  COMPLETED: 4,
  CANCELED: 5,
  REJECTED: 6,
  FINISH: 7
};

// legacy numeric → text (for writes)
const STATUS_NUM_TO_TEXT: Record<number, string> = Object.fromEntries(
  Object.entries(STATUS_TEXT_TO_NUM).map(([text, num]) => [num, text])
);

export const toWireStatus = (numeric: number): string =>
  STATUS_NUM_TO_TEXT[numeric] ?? String(numeric);

// text plan.type → legacy numeric (constants/profile PLAN_TYPE_VALUES)
const PLAN_TYPE_TO_NUM: Record<string, number> = {
  video_call: 1,
  report: 2
};

// legacy numeric plan_type → v2 text slug (for the list filter)
const PLAN_TYPE_NUM_TO_TEXT: Record<string, string> = {
  '1': 'video_call',
  '2': 'report'
};

const legacyStatus = (status: unknown): number => {
  if (typeof status === 'number') return status;

  return STATUS_TEXT_TO_NUM[status as string] ?? 0;
};

const legacyPlanType = (type: unknown): number => {
  if (typeof type === 'number') return type;

  return PLAN_TYPE_TO_NUM[type as string] ?? 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LegacyAppointment = Record<string, any>;

// nested note/file rows serialize camelCase createdAt in v2; components read
// created_at (from the GQL fragments).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withLegacyDate = (r: any) =>
  r ? { ...r, created_at: r.created_at ?? r.createdAt } : r;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toLegacyAppointment = (row: any): LegacyAppointment => {
  if (!row) return row;

  const plan = row.plan
    ? { ...row.plan, type: legacyPlanType(row.plan.type) }
    : row.plan;

  const notes = (row.notes ?? row.appointmentNotes ?? []).map(withLegacyDate);
  const files = (row.files ?? row.appointmentFiles ?? []).map(withLegacyDate);

  // embedded report / report-file are raw v2 entities (camelCase); expose
  // the snake keys the ReportCard/Reports consumers read. `status` stays the
  // v2 string enum — comparisons use REPORT_STATUS (string), not the legacy
  // numeric REPORT_STATUSES.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const report = row.report
    ? {
        ...row.report,
        id: row.report.id,
        date_report: row.report.date_report ?? row.report.dateReport,
        created_at: row.report.created_at ?? row.report.createdAt,
        appointment_id: row.report.appointment_id ?? row.report.appointmentId,
        first_name: row.report.first_name ?? row.report.firstName,
        last_name: row.report.last_name ?? row.report.lastName,
        doctor_info: row.report.doctor_info ?? row.report.doctorInfo,
        date_of_birth: row.report.date_of_birth ?? row.report.dateOfBirth
      }
    : null;

  const reportsFileRaw = Array.isArray(row.reportsFiles)
    ? row.reportsFiles[0] ?? null
    : row.reports_file ?? null;

  const reports_file = reportsFileRaw ? withLegacyDate(reportsFileRaw) : null;

  return {
    ...row,
    id: String(row.id),
    // additive public id (encrypted) the BE returns alongside the int id —
    // used for the meeting room + shared meeting URL, never for :id routes
    hashId: row.hashId ?? row.hash_id ?? null,
    status: legacyStatus(row.status),
    // legacy admin_paid ≡ v2 `paid` (flag-back #18 note)
    admin_paid: row.admin_paid ?? row.paid ?? false,
    plan,
    // relation renames
    dates: row.dates ?? row.appointmentDates ?? [],
    notes,
    files,
    appointment_translators:
      row.appointment_translators ?? row.appointmentTranslators ?? [],
    reports_file,
    report,
    doctor_invoice: row.doctor_invoice ?? null
  };
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export type AppointmentsQuery = {
  page?: number;
  limit?: number;
  name?: string;
  status?: number;
  plan_type?: string;
  patient?: number | string;
  admin_paid?: number;
  withReport?: number;
  invoiced?: number;
  startDate?: string;
  endDate?: string;
};

// GET /appointments — [rows, total] tuple (flag-back #11). Filters map to the
// v2 Query DTO: status is sent as the text enum, plan_type stays a slug.
const buildAppointmentParams = (query: AppointmentsQuery) => ({
  page: query.page,
  limit: query.limit,
  name: query.name || undefined,
  status: query.status ? toWireStatus(query.status) : undefined,
  // filter arrives as a numeric-string ('1'/'2'); v2 filters on the text slug
  plan_type: query.plan_type
    ? PLAN_TYPE_NUM_TO_TEXT[query.plan_type] ?? query.plan_type
    : undefined,
  patient: query.patient || undefined,
  admin_paid: query.admin_paid,
  withReport: query.withReport,
  invoiced: query.invoiced,
  startDate: query.startDate || undefined,
  endDate: query.endDate || undefined
});

export const fetchAppointments = async (
  query: AppointmentsQuery
): Promise<{ results: LegacyAppointment[]; total: number }> => {
  const res = await instance.get<[unknown[], number]>('appointments', {
    params: buildAppointmentParams(query)
  });

  const [rows, total] = res.data;

  return { results: (rows || []).map(toLegacyAppointment), total };
};

export const fetchAppointmentById = async (
  id: number | string
): Promise<LegacyAppointment> => {
  const res = await instance.get(`appointments/${id}`);

  return toLegacyAppointment(res.data);
};

// GET /appointments/nearest → an AppointmentDate row with a joined
// `appointment`. Legacy NearestAppointment.data was {date, appointment_id,
// patient, plan} — flatten to that.
export const fetchNearestAppointment =
  async (): Promise<LegacyAppointment | null> => {
    const res = await instance.get('appointments/nearest');
    const row = res.data;

    if (!row) return null;

    const appointment = row.appointment ?? {};

    return {
      date: row.date,
      appointment_id: String(row.appointmentId ?? appointment.id ?? ''),
      patient: appointment.patient ?? {},
      plan: appointment.plan ?? {}
    };
  };

// GET /appointments/creator (+ /creator-pay) — doctor-created appointments.
export const fetchCreatorAppointments = async (
  query: AppointmentsQuery & { pay?: boolean }
): Promise<{ results: LegacyAppointment[]; total: number }> => {
  const res = await instance.get<[unknown[], number]>(
    query.pay ? 'appointments/creator-pay' : 'appointments/creator',
    { params: buildAppointmentParams(query) }
  );

  const [rows, total] = res.data;

  return { results: (rows || []).map(toLegacyAppointment), total };
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
// PUT /appointments/:id/status — {status, reason?} (RejectStatus + finish +
// accept all route here). `status` arg is the legacy numeric value.
export const updateAppointmentStatus = (
  id: number | string,
  status: number,
  reason?: string
) =>
  instance.put(`appointments/${id}/status`, {
    status: toWireStatus(status),
    ...(reason !== undefined ? { reason } : {})
  });

export const deleteAppointment = (id: number | string) =>
  instance.delete(`appointments/${id}`);

export const finishAppointment = (id: number | string) =>
  instance.put(`appointments/${id}/finish`);

export const approveAppointmentDate = (
  id: number | string,
  dateId: number | string
) => instance.put(`appointments/${id}/dates/${dateId}/approve`);

export const requestAppointmentDate = (id: number | string, date: string) =>
  instance.post(`appointments/${id}/dates`, { date });

export const createAppointmentNote = (id: number | string, note: string) =>
  instance.post(`appointments/${id}/notes`, { note });

export const deleteAppointmentNote = (
  id: number | string,
  noteId: number | string
) => instance.delete(`appointments/${id}/notes/${noteId}`);

export const createAppointmentFiles = (id: number | string, files: number[]) =>
  instance.post(`appointments/${id}/files`, { files });

export type CreateAppointmentPayload = {
  patientId: number;
  planId?: number;
  reason?: string;
  status?: number;
};

export const createAppointment = (payload: CreateAppointmentPayload) =>
  instance.post('appointments', {
    ...payload,
    status: payload.status ? toWireStatus(payload.status) : undefined
  });

// POST /appointments/creator {notes?} — doctor-initiated session (no patient/
// plan, status APPROVED, creator=doctor, paid). Returns the created row.
export const createCreatorAppointment = async (
  notes?: string
): Promise<LegacyAppointment> => {
  const res = await instance.post('appointments/creator', { notes });

  return toLegacyAppointment(res.data);
};

// ---------------------------------------------------------------------------
// Meetings (REST only — jitsi/webrtc socket flow is untouched)
// ---------------------------------------------------------------------------
// POST /meetings {id} → {redirect_url, meetUrl(32-char hash, pass as-is)}
export const openRoom = async (
  id: number | string
): Promise<{ redirect_url: string; meetUrl: string }> => {
  const res = await instance.post<{ redirect_url: string; meetUrl: string }>(
    'meetings',
    { id }
  );

  return res.data;
};

// POST /meetings/create-room {appointmentId} → {appointment_id, password}
export const createRoom = async (
  appointmentId: number | string
): Promise<{ appointment_id: number; password: string }> => {
  const res = await instance.post<{
    appointment_id: number;
    password: string;
  }>('meetings/create-room', { appointmentId: appointmentId });

  return res.data;
};

// ---------------------------------------------------------------------------
// Invoice
// ---------------------------------------------------------------------------
export type InvoiceTemplate = {
  id: number;
  name?: string;
  keys?: {
    id: number;
    label: string;
    type: number;
    required: boolean;
  }[];
};

export const fetchInvoiceTemplate =
  async (): Promise<InvoiceTemplate | null> => {
    const res = await instance.get<InvoiceTemplate | null>(
      'invoice/template/doctor'
    );

    return res.data ?? null;
  };

// POST /invoice — the per-appointment doctor invoice, BOTH modes (#20):
// template ({type, fields}) and file ({type, file}). Writes
// appointment_doctor_invoice → shows as doctor_invoice on the detail/list
// (#18/#21) and feeds the invoice PDF (#6b).
// (POST /invoice/values is a DIFFERENT table — the org template editor —
//  do NOT use it for per-appointment invoices.)
export const createInvoice = (payload: {
  appointment_id: number | string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields?: any;
  file?: string;
}) =>
  instance.post('invoice', {
    ...payload,
    appointment_id: Number(payload.appointment_id)
  });

export type { Paginated };
